import Hls from "hls.js";
import { useEffect, useRef, useState } from "react";

/**
 * What a listener sees when the host is broadcasting from the studio.
 *
 * ## Why this is not the dock
 *
 * In catalogue mode the room tells the dock which track to play and the dock
 * fetches it from the CDN, which is why a broadcast survives navigating away.
 * In studio mode there is no track to fetch: the audio the host mixed and the
 * picture they composited arrive together as one HLS stream from the ingest
 * server, and it has a picture, which the dock has nowhere to put.
 *
 * So this element owns playback for the duration, and `RoomAudio` stands down -
 * see the note there. Two players fed from the same room would mean hearing the
 * track twice, a second or so apart.
 *
 * ## Muted and playing, with one button to join
 *
 * Browsers refuse to autoplay audible media, and a video that silently fails to
 * start looks like a broken stream rather than a policy. So it starts muted and
 * playing - which *is* allowed, and which proves the stream is live before
 * anybody commits to it.
 *
 * Turning that into sound used to be two controls in two places: "Listen live"
 * beside the video to join the room, and an Unmute button on it. They are one
 * intention. There is now a play button in the middle of the picture, where
 * every video on the internet keeps it, and pressing it does the whole thing -
 * unmutes, seeks to the live edge, and puts the listener in the room so they
 * are counted and can talk.
 *
 * Once they are listening the button gets out of the way and returns on hover
 * as pause, which is the same convention from the other direction.
 *
 * ## The stream can be live before it is playable
 *
 * There is a window after the host presses start where the room says stream
 * mode and the ingest server has not cut a segment yet, so the playlist 404s.
 * That is normal and brief, and it is why a network error here retries rather
 * than reporting a failure at somebody who has done nothing wrong.
 */
/**
 * How far behind the live edge a listener sits, in seconds.
 *
 * This is the cushion. Everything below it is a stall the listener sees; the
 * only cost of raising it is being further behind the host, and a music
 * broadcast with a chat beside it is comfortable at several seconds - Twitch
 * runs considerably more.
 */
const TARGET_LATENCY = 6;

export function StreamPlayer({
  url,
  listening,
  onJoin,
  onLeave,
}: {
  url: string;
  /** Whether the viewer is in the room, which the play button toggles. */
  listening: boolean;
  onJoin: () => void;
  onLeave: () => void;
}) {
  const ref = useRef<HTMLVideoElement | null>(null);
  const [muted, setMuted] = useState(true);
  const [volume, setVolume] = useState(1);
  const [paused, setPaused] = useState(false);
  const [waiting, setWaiting] = useState(true);
  /*
   * What went wrong, in the player's own words.
   *
   * This used to be swallowed: a network error retried forever, a media error
   * called `recoverMediaError()` forever, and anything else destroyed the
   * instance in silence. All three produced the same screen - "connecting..."
   * that never finishes - which is indistinguishable from a host who has not
   * started yet, and impossible to diagnose without attaching a debugger to
   * somebody else's browser.
   *
   * hls.js's `details` string is precise and short (`bufferAppendError`,
   * `manifestIncompatibleCodecsError`, `fragParsingError`), so it goes on the
   * screen. A listener does not know what it means; the host reporting the
   * problem can read it out, and that is the point.
   */
  const [failed, setFailed] = useState<string | null>(null);

  useEffect(() => {
    const video = ref.current;

    if (!video) return;

    setWaiting(true);
    setFailed(null);

    /*
     * hls.js first, native second - and that order is the whole fix.
     *
     * Asking `canPlayType('application/vnd.apple.mpegurl')` first looks right
     * and is a trap: Chromium answers `"maybe"`, which is truthy, and then
     * fails to demux the playlist it was just handed. The symptom is a video
     * element that reports `readyState 4` and a decoded 960x540 frame while
     * sitting at `currentTime 0` forever, with
     * `DEMUXER_ERROR_COULD_NOT_PARSE` hidden in `video.error`.
     *
     * Where native HLS is genuinely the better engine - iOS Safari, which has
     * no MSE at all - `Hls.isSupported()` is false, so the fallback below is
     * reached for exactly the platforms that need it.
     */
    if (!Hls.isSupported()) {
      if (!video.canPlayType("application/vnd.apple.mpegurl")) return undefined;

      video.src = url;

      return () => {
        video.removeAttribute("src");
        video.load();
      };
    }

    const hls = new Hls({
      /*
       * Low-latency mode off, deliberately.
       *
       * MediaMTX publishes LL-HLS - quarter-second parts, and a playlist
       * request the server holds open until the next one is ready. That
       * requires HTTP/2, because on HTTP/1.1 those blocking requests
       * permanently occupy connections out of the six a browser allows per
       * origin and the media parts queue behind them.
       *
       * We cannot have HTTP/2: `mod_http2` does not work under `mpm_prefork`,
       * and `mpm_prefork` is what `mod_php` requires. So the choice is not
       * between low latency and lower latency, it is between a stream that
       * plays and one that spins.
       *
       * hls.js ignores the parts in this mode and uses whole segments, which
       * MediaMTX is cutting at one second - so `liveSyncDurationCount: 3` puts
       * the playhead about three seconds behind the host. For a set with a
       * chat beside it that is well inside normal; Twitch runs several times
       * that.
       *
       * Turn this back on if the platform ever moves to PHP-FPM and HTTP/2.
       */
      lowLatencyMode: false,

      /*
       * Six segments back, not three.
       *
       * Three was chosen to keep the host close to their listeners and it left
       * no margin whatsoever. Measured on production, idle: the buffer ran from
       * about three seconds down to zero and back, over and over, with `waiting`
       * events every twenty or thirty seconds - and far more often on a machine
       * doing anything else at the same time. The stream was not dropping; the
       * player simply had nothing in hand.
       *
       * At one-second segments this puts a listener about six seconds behind the
       * host and gives them six seconds of cushion to lose before they hear it.
       * Twitch runs several times that. The server publishes a fifteen-second
       * window to buffer from - see `docker/mediamtx/mediamtx.yml`, and change
       * the two together.
       */
      liveSyncDuration: 6,

      /*
       * Seconds, not segments.
       *
       * `liveSyncDurationCount` counts *target durations*, and the target
       * duration is whatever the playlist declares - MediaMTX says 2 even when
       * it is cutting at 1. So "6" meant something different from what it
       * looked like, and measured out at two to four seconds rather than six.
       * The two options are mutually exclusive; this is the one that says what
       * it means.
       */
      liveMaxLatencyDuration: 15,

      /*
       * Catch up by playing very slightly faster rather than by seeking.
       *
       * A listener who falls behind - a stall, a slow segment - has to get back
       * to the edge somehow. Left alone hls.js seeks, which is an audible jump
       * mid-sentence. Five percent is inaudible and closes a two-second gap in
       * forty seconds without anybody noticing it happened.
       */
      maxLiveSyncPlaybackRate: 1.05,

      // Room to hold the cushion above, plus what arrives while it is held.
      maxBufferLength: 30,
      backBufferLength: 30,
      enableWorker: true,
    });

    /*
     * Bounded retries, and then the truth.
     *
     * The first errors after a host presses start are expected and harmless -
     * the playlist does not exist until the ingest server has cut a segment -
     * so retrying is right. Retrying *forever* is not: it turns a permanent
     * fault into a permanent spinner.
     */
    let networkRetries = 0;
    let mediaRecoveries = 0;

    hls.on(Hls.Events.ERROR, (_event, data) => {
      // Logged whether fatal or not. Non-fatal errors are usually noise, and
      // occasionally they are the only clue - a codec the browser will not
      // append shows up here long before anything else notices.
      console.warn(
        "[stream]",
        data.type,
        data.details,
        data.fatal ? "(fatal)" : "",
      );

      if (!data.fatal) return;

      switch (data.type) {
        case Hls.ErrorTypes.NETWORK_ERROR:
          networkRetries += 1;

          if (networkRetries > 20) {
            hls.destroy();
            setFailed(data.details);

            return;
          }

          hls.startLoad();
          break;

        case Hls.ErrorTypes.MEDIA_ERROR:
          mediaRecoveries += 1;

          if (mediaRecoveries > 3) {
            hls.destroy();
            setFailed(data.details);

            return;
          }

          // The second attempt swaps the audio codec, which is the documented
          // escape from an fMP4 whose audio the browser cannot append.
          if (mediaRecoveries === 2) hls.swapAudioCodec();

          hls.recoverMediaError();
          break;

        default:
          hls.destroy();
          setFailed(data.details);
      }
    });

    /*
     * Start a few seconds behind the live edge, deliberately.
     *
     * ## Why this is not left to hls.js
     *
     * At the live edge the buffer cannot be deeper than the time since the last
     * segment was published - there is nothing further ahead to fetch. So a
     * player sitting at the edge runs with a fraction of a second in hand
     * however the config is written, and every network hiccup underruns it.
     * Measured on production, idle: the buffer sawtoothed to zero every few
     * seconds and the listener saw "connecting" repeatedly.
     *
     * `liveSyncDuration` is supposed to place it further back and did not do so
     * reliably here - hls.js resolves the live position from the playlist's
     * declared target duration, which MediaMTX reports as 2 even when cutting
     * at 1, so the arithmetic never matched the intent. Rather than tune around
     * that, this seeks once to a distance measured in seconds off the actual
     * seekable end, which is a fact rather than an inference.
     *
     * Attempted on each playlist update until it lands: a listener who joins a
     * broadcast that started ten seconds ago has nowhere to sit back to yet, and
     * should be placed properly once the window has grown rather than left at
     * the edge for the rest of the set.
     */
    let positioned = false;

    hls.on(Hls.Events.LEVEL_UPDATED, () => {
      if (positioned || video.seekable.length === 0) return;

      const edge = video.seekable.end(video.seekable.length - 1);

      // Not enough stream yet to sit back into. Try again next update.
      if (edge < TARGET_LATENCY + 1) return;

      positioned = true;

      // Only ever backwards. Somebody who has drifted further back than this is
      // where they chose to be.
      if (video.currentTime > edge - TARGET_LATENCY) {
        video.currentTime = edge - TARGET_LATENCY;
      }
    });

    hls.loadSource(url);
    hls.attachMedia(video);

    return () => hls.destroy();
  }, [url]);

  useEffect(() => {
    const video = ref.current;

    if (!video) return;

    video.muted = muted;
    video.volume = volume;

    // Not while deliberately paused, or changing the volume would restart a
    // broadcast somebody stopped.
    if (paused) return;

    void video.play().catch(() => {
      // Autoplay refused even muted, which some configurations do. The centre
      // play button is what the listener sees, and their click starts it.
    });
  }, [muted, volume, paused]);

  /**
   * Start listening properly, from a real click.
   *
   * The seek matters: somebody who has been watching muted has been drifting
   * behind the whole time, and a broadcast is worth joining at the point it has
   * reached rather than wherever their buffer happens to sit.
   */
  const join = () => {
    const video = ref.current;

    setMuted(false);
    setPaused(false);

    if (video) {
      if (video.seekable.length > 0) {
        const edge = video.seekable.end(video.seekable.length - 1);

        if (edge - video.currentTime > TARGET_LATENCY + 2) {
          video.currentTime = Math.max(0, edge - TARGET_LATENCY);
        }
      }

      void video.play().catch(() => {});
    }

    if (!listening) onJoin();
  };

  const leave = () => {
    setPaused(true);
    ref.current?.pause();

    if (listening) onLeave();
  };

  /*
   * Listening means all three: in the room, audible, and running. Anything less
   * is a state the play button should be offering to fix.
   */
  const active = listening && !muted && !paused;

  return (
    <div
      className="group relative w-full overflow-hidden rounded-[20px] border border-[#26264e] bg-black"
      data-stream-player
    >
      <video
        ref={ref}
        // `playsInline` or iOS takes the stream fullscreen the moment it plays,
        // which throws the listener out of the page they were reading.
        playsInline
        autoPlay
        muted
        className="block aspect-video w-full"
        data-stream-video
        onPlaying={() => {
          setWaiting(false);
          setPaused(false);
        }}
        onWaiting={() => setWaiting(true)}
        // Chrome pauses media for reasons of its own - a device change, a
        // policy - and the button has to tell the truth about it.
        onPause={() => setPaused(true)}
      />

      {/*
        The one control that matters, in the middle of the picture.

        Visible whenever the listener is not fully in - muted, paused, or not in
        the room - and on hover once they are, so pausing is where they will
        reach for it. Hidden the rest of the time, because a permanent button
        over a broadcast is a permanent thing in the way of it.
      */}
      {!failed && !waiting && (
        <button
          type="button"
          onClick={active ? leave : join}
          aria-label={active ? "Pause" : "Listen"}
          data-stream-toggle
          className={`absolute inset-0 flex items-center justify-center transition-opacity ${
            active
              ? "opacity-0 focus-visible:opacity-100 group-hover:opacity-100"
              : "opacity-100"
          }`}
        >
          <span
            className={`flex h-20 w-20 items-center justify-center rounded-full backdrop-blur-sm transition-transform hover:scale-105 ${
              active ? "bg-black/55" : "bg-ink/95"
            }`}
          >
            {active ? (
              <svg
                viewBox="0 0 24 24"
                className="h-8 w-8 fill-white"
                aria-hidden
              >
                <rect x="6" y="4.5" width="4" height="15" rx="1" />
                <rect x="14" y="4.5" width="4" height="15" rx="1" />
              </svg>
            ) : (
              // Nudged right: a triangle centred on its bounding box reads as
              // sitting left of centre inside a circle.
              <svg
                viewBox="0 0 24 24"
                className="ml-1 h-9 w-9 fill-app"
                aria-hidden
              >
                <path d="M7 4.5v15l13-7.5z" />
              </svg>
            )}
          </span>
        </button>
      )}

      {failed ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/75 px-6 text-center">
          <span className="meta-caps text-hot-text">
            the stream could not be played
          </span>
          <span className="font-mono text-[10px] text-ghost" data-stream-error>
            {failed}
          </span>
        </div>
      ) : (
        waiting && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/60">
            <span className="meta-caps text-ghost">connecting…</span>
          </div>
        )
      )}

      {/*
        Over the picture rather than under it, because that is where a video's
        controls belong and because the space beside the player is worth more to
        the room than to a volume slider.
      */}
      {/*
        Volume, and only once they are in.
        
        Before joining, the play button in the middle is the single thing to
        press. An Unmute here alongside it would be a second way to ask for
        sound that skipped putting the listener in the room - so they would be
        hearing a broadcast while its listener count and chat had no idea they
        were there.
      */}
      {!failed && listening && (
        <div className="absolute inset-x-0 bottom-0 flex items-center gap-3 bg-gradient-to-t from-black/80 to-transparent px-4 pb-3 pt-8">
          <button
            type="button"
            onClick={() => setMuted((on) => !on)}
            aria-pressed={!muted}
            aria-label={muted ? "Unmute" : "Mute"}
            data-stream-unmute
            className="shrink-0 rounded-[var(--radius-pill)] bg-ink px-4 py-1.5 text-[13px] font-bold text-app transition-opacity hover:opacity-90"
          >
            {muted ? "Unmute" : "Mute"}
          </button>

          <label className="flex flex-1 items-center gap-2">
            <span className="sr-only">Volume</span>
            <input
              type="range"
              min={0}
              max={1}
              step={0.01}
              value={muted ? 0 : volume}
              data-stream-volume
              onChange={(e) => {
                const next = Number(e.target.value);

                setVolume(next);
                // Dragging the slider up is how people unmute, whatever the
                // button says.
                if (next > 0) setMuted(false);
              }}
              className="w-full max-w-[160px] accent-[var(--color-ink)]"
            />
          </label>

          <span className="meta-caps shrink-0 text-white/70">live</span>
        </div>
      )}
    </div>
  );
}
